import React from 'react';

import { classnames, padding, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { mdiClose, mdiHelpCircleOutline } from '@lumapps/lumx/icons';
import {
    Alignment,
    ColorPalette,
    Emphasis,
    FlexBox,
    IconButton,
    IconButtonProps,
    Orientation,
    Placement,
    PopoverDialog,
    PopoverDialogProps,
    Size,
    Text,
    Theme,
    TooltipPlacement,
    Typography,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { generateUUID } from '@lumapps/utils/string/generateUUID';

import './index.scss';

const BASE_CLASSNAME = 'lumx-toggle-tip-button';
export interface ToggleTipButtonProps {
    /** Classname to pass to the component */
    className?: string;
    /** Content displayed inside the popover */
    content: string | React.ReactNode;
    /** Icon to display */
    icon?: string;
    /**
     * Color of the icon button
     * @default 'dark'
     */
    iconColor?: ColorPalette | undefined;
    /** Props to be passed down to the icon button */
    iconButtonProps?: Partial<IconButtonProps>;
    /** Label that will be displayed inside tooltip */
    label?: string;
    /** Props to be passed down to the popover */
    popoverDialogProps?: Partial<Omit<PopoverDialogProps, 'aria-labelledby'>>;
    /**
     * Placement of the popover relative to the anchor.
     * @default 'bottom'
     */
    popoverPlacement?: Placement;
    /**
     * Placement of the tooltip relative to the anchor.
     * @default 'top'
     */
    tooltipPlacement?: TooltipPlacement;
    /** Scope use to setup the data-id correctly */
    scope?: string;
    /**
     * Defines the trigger method of the toggle tip.
     * @default 'click'
     */
    mode?: 'click' | 'hover';
    /**
     * Theme that will be applied to the popover, text content and close icon. Either Theme.dark or Theme.light
     * @default Theme.dark
     */
    theme?: Theme;
    /** callback when toggle tip button ( like fetch some information ..)
     * @default undefined
     * */
    onToggleClick?: () => void;
}

/**
 * Component that displays an Icon Button that, upon clicking on the button, it triggers a popover dialog
 * in order to display additional data.
 *
 * @family Buttons
 * @param ToggleTipButtonProps
 * @returns ToggleTipButton
 */
export const ToggleTipButton: React.FC<ToggleTipButtonProps> = ({
    className,
    content,
    icon = mdiHelpCircleOutline,
    iconColor = ColorPalette.dark,
    iconButtonProps,
    label,
    popoverDialogProps,
    popoverPlacement = Placement.BOTTOM,
    tooltipPlacement = Placement.TOP,
    scope = 'toogle-tip',
    mode = 'click',
    theme = Theme.dark,
    onToggleClick,
}) => {
    const { block, element } = useClassnames(BASE_CLASSNAME);
    const { get } = useDataAttributes(scope);
    const [isOpen, toggle, close, open] = useBooleanState(false);
    const { translateKey } = useTranslate();
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const ariaLabelledById = `${BASE_CLASSNAME}-${generateUUID()}`;

    const handleOnClick = (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (onToggleClick) {
            onToggleClick();
        }
        toggle();
    };

    return (
        <>
            <IconButton
                className={block([className])}
                tooltipProps={{ placement: tooltipPlacement }}
                onMouseEnter={mode === 'hover' ? open : null}
                onMouseLeave={mode === 'hover' ? close : null}
                {...iconButtonProps}
                color={iconColor}
                emphasis={Emphasis.low}
                icon={icon}
                id={ariaLabelledById}
                label={label || translateKey(GLOBAL.MORE_INFORMATION)}
                aria-haspopup="dialog"
                // Hide the label tooltip on hover mode to avoid having 2 tooltips displayed
                hideTooltip={mode === 'hover'}
                onClick={handleOnClick}
                ref={anchorRef}
                {...get({ element: 'button', action: 'open-toggle-tip' })}
            />

            <PopoverDialog
                className={element('popover')}
                {...popoverDialogProps}
                anchorRef={anchorRef}
                aria-labelledby={ariaLabelledById}
                fitWithinViewportHeight
                focusAnchorOnClose
                hasArrow={mode === 'click'}
                isOpen={isOpen}
                onClose={close}
                placement={popoverPlacement}
                theme={theme}
            >
                <FlexBox
                    className={classnames(padding('all', Size.big))}
                    orientation={Orientation.horizontal}
                    gap={Size.regular}
                    hAlign={Alignment.top}
                    vAlign="space-between"
                >
                    {typeof content === 'string' ? (
                        <Text
                            as="p"
                            color={theme === Theme.dark ? ColorPalette.light : ColorPalette.dark}
                            typography={Typography.body1}
                        >
                            {translateKey(content)}
                        </Text>
                    ) : (
                        content
                    )}
                    {mode === 'click' && (
                        <IconButton
                            className={element('close')}
                            color={theme === Theme.dark ? ColorPalette.light : ColorPalette.dark}
                            emphasis={Emphasis.low}
                            icon={mdiClose}
                            label={translateKey(GLOBAL.CLOSE)}
                            onClick={handleOnClick}
                            size={Size.m}
                            {...get({ element: 'button', action: 'close-toggle-tip' })}
                        />
                    )}
                </FlexBox>
            </PopoverDialog>
        </>
    );
};
